import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Clock, XCircle, CheckCircle } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import { notificationsAPI } from '../../services/api';
import { COLORS } from '../../utils/constants';

const C = COLORS;

const typeConfig = {
  info: { bg: C.infoLight, color: C.info, icon: AlertCircle },
  warning: { bg: C.warningLight, color: C.warning, icon: Clock },
  danger: { bg: C.dangerLight, color: C.danger, icon: XCircle },
  success: { bg: C.successLight, color: C.success, icon: CheckCircle },
};

const NotificationsPage = () => {
  const [data, setData] = useState([]);
  const [unread, setUnread] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const res = await notificationsAPI.getAll({ limit: 50 });
      setData(res.data.data);
      setUnread(res.data.unread);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggle = async (id, isRead) => {
    try {
      if (!isRead) await notificationsAPI.markRead(id);
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleMarkAll = async () => {
    try { await notificationsAPI.markAllRead(); fetchData(); } catch (err) { console.error(err); }
  };

  const timeSince = (date) => {
    const s = Math.floor((Date.now() - new Date(date)) / 1000);
    if (s < 60) return 'Just now';
    if (s < 3600) return `${Math.floor(s / 60)} min ago`;
    if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
    return `${Math.floor(s / 86400)} days ago`;
  };

  return (
    <AppLayout title="Notifications">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.g900 }}>Notifications</h2>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: C.g500 }}>{unread} unread</p>
        </div>
        <button onClick={handleMarkAll}
          style={{ padding: '8px 16px', border: `1px solid ${C.g200}`, borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 13, color: C.g600, fontFamily: 'inherit' }}>
          Mark all as read
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}`, overflow: 'hidden' }}>
        {data.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: C.g400 }}>
            <CheckCircle size={48} style={{ marginBottom: 12, color: C.g300 }} />
            <p style={{ fontSize: 16, fontWeight: 600 }}>No notifications yet</p>
          </div>
        ) : data.map((n, i) => {
          const tc = typeConfig[n.type] || typeConfig.info;
          const Icon = tc.icon;
          return (
            <div key={n.id} onClick={() => handleToggle(n.id, n.is_read)}
              style={{ display: 'flex', gap: 14, padding: '18px 24px', borderBottom: i < data.length - 1 ? `1px solid ${C.g100}` : 'none',
                background: n.is_read ? '#fff' : C.primaryGhost, cursor: 'pointer', alignItems: 'flex-start', transition: 'background 0.15s' }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: tc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: tc.color }}>
                <Icon size={18} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: n.is_read ? 500 : 700, color: C.g900 }}>{n.title}</p>
                  {!n.is_read && <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.primary, flexShrink: 0 }} />}
                </div>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: C.g500, lineHeight: 1.5 }}>{n.description}</p>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: C.g400 }}>{timeSince(n.created_at)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </AppLayout>
  );
};

export default NotificationsPage;
